import React, { createContext, ReactNode, useContext } from "react";
import directusClient from "../directus/client";
import { useAuth } from "../hooks/auth.hooks";

// Types
export interface User {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  role?: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRefreshing?: boolean;
  error: string | null;
}

// Create context type
interface DirectusContextType {
  client: typeof directusClient;
  accessToken: string | null;
  refreshToken: string | null;
  auth: {
    state: AuthState;
    loginUser: (
      credentials: LoginCredentials
    ) => Promise<{ success: boolean; error?: string }>;
    logoutUser: () => Promise<{ success: boolean; error?: string }>;
    refreshAccessToken: () => Promise<string | null> | Promise<Promise<string | null>> ;
    checkAuthStatus: () => Promise<void>;
    clearError: () => void;
  };
}

// Create the context
const DirectusContext = createContext<DirectusContextType | undefined>(
  undefined
);

// Provider props interface
interface DirectusProviderProps {
  children: ReactNode;
}

// Provider component
export const DirectusProvider: React.FC<DirectusProviderProps> = ({
  children,
}) => {
  const auth = useAuth(directusClient, {
    verbose: false,
    traceKey: "DirectusProvider",
  });

  const value = {
    client: directusClient,
    accessToken: auth.data?.access_token || null,
    refreshToken: auth.data?.refresh_token || null,
    auth: {
      state: { ...auth.state },
      loginUser: auth.loginUser,
      logoutUser: auth.logoutUser,
      refreshAccessToken: auth.refreshAccessToken,
      checkAuthStatus: auth.checkAuthStatus,
      clearError: auth.clearError,
    },
  };

  return (
    <DirectusContext.Provider value={value}>
      {children}
    </DirectusContext.Provider>
  );
};

// Custom hook to use Directus client
export const useDirectus = (): DirectusContextType => {
  const context = useContext(DirectusContext);

  if (context === undefined) {
    throw new Error("useDirectus must be used within a DirectusProvider");
  }

  return context;
};

// // Custom hook to use auth (for convenience)
export const useDirectusAuth = () => {
  const { auth } = useDirectus();
  return auth;
};

// export default DirectusProvider;
